import type { ReactNode } from 'react';
import { brandConfig } from '../../../config/brands';
import { SingleSelectDropdown } from '../../../components/common/SingleSelectDropdown';
import type { BrandLogo } from '../../../features/brands/types';
import { BrandLogoChip } from './BrandLogoChip';
import {
  INDUSTRY_CATEGORIES,
  INDUSTRY_CATEGORY_LABELS,
  type IndustryCategory,
} from '../../../features/products/industry';
import type { ComplianceSettingsResponse } from '../../../features/compliance/api';
// react-refresh/only-export-components (Fast Refresh はコンポーネントのみを export する
// ファイルでないと効かない) を満たすため、関数ヘルパーは別ファイルに分離している。
import { complianceCandidateRulesets } from './complianceCandidates';

export interface ProductInfoEditForm {
  title: string;
  description: string;
  sku: string;
  barcode_value: string;
  brand_id: string;
  brand_name: string;
  product_type: string;
  category: string;
  industry_categories: IndustryCategory[];
  compliance_defaults: Record<string, boolean>;
}

interface ProductInfoEditTableProps {
  editForm: ProductInfoEditForm;
  setEditForm: (form: any) => void;
  validationErrors: Set<string>;
  /** logos は GET /brands が付ける紐付けロゴ (署名 URL 入り)。あればドロップダウンで名前の左に代表ロゴを出す */
  brands: { id: string; brand_name: string; logos?: BrandLogo[] }[];
  complianceSettings: ComplianceSettingsResponse | undefined;
  onCreateBrand: () => void;
  /**
   * ブランドの行を商品名より上 (先頭) に置く (新規登録・入口フロー文書 §2 案B / 再構成計画 §2.4)。
   * 既存案件の編集 (詳細ページ) は従来の位置のまま
   */
  brandFirst?: boolean;
  /** ブランドの行の下に出す補足 (新規ブランドの確認など) */
  brandNote?: ReactNode;
  /** ブランドのプルダウンの真下に重ねて出す吹き出し (登録完了の通知など。行の高さは変えない) */
  brandBubble?: ReactNode;
  /** 「ブランド」の見出しの右に出すヘルプ (HelpTooltip) */
  brandHelp?: ReactNode;
}

/**
 * 商品情報編集テーブル (<tr> 群)。
 * ManualProductDetailPage.tsx の renderEditRows を純粋に抽出したコンポーネント
 * (計画書§4.1)。ManualProductDetailPage (詳細ページ) と ManualProductFormModal
 * (商品追加モーダル) の両方から `<table className="details-table"><tbody>` の
 * 中身として使う。見た目・挙動は抽出前と同一。
 */
export function ProductInfoEditTable({
  editForm,
  setEditForm,
  validationErrors,
  brands,
  complianceSettings,
  onCreateBrand,
  brandFirst = false,
  brandNote,
  brandBubble,
  brandHelp,
}: ProductInfoEditTableProps) {
  const update = (field: string, value: string) => setEditForm({ ...editForm, [field]: value });
  const complianceCandidates = complianceCandidateRulesets(
    complianceSettings,
    editForm.industry_categories,
  );
  const updateComplianceDefault = (code: string, value: boolean) => {
    const next: Record<string, boolean> = { ...editForm.compliance_defaults, [code]: value };
    setEditForm({ ...editForm, compliance_defaults: next });
  };
  const toggleIndustry = (key: IndustryCategory, checked: boolean) => {
    const next = checked
      ? [...editForm.industry_categories, key]
      : editForm.industry_categories.filter((k) => k !== key);
    setEditForm({ ...editForm, industry_categories: next });
  };
  const brandRow = (
    <>
      {/* ブランド (マスター) + ブランド（自由入力）。選ぶと自由入力にその名前が入り、選ばなければ自由入力の値が表示名になる */}
      <tr>
        <th>ブランド{brandHelp}</th>
        <td className={brandBubble ? 'brand-bubble-anchor' : undefined}>
          <SingleSelectDropdown
            label="ブランド"
            labelOnly
            alwaysShowSelected
            unstyledTrigger
            flipUp
            triggerClassName="inline-edit-dropdown-trigger"
            value={editForm.brand_id}
            options={[
              { value: '', label: '未選択' },
              ...brands.map((b) => ({ value: b.id, label: b.brand_name, iconNode: <BrandLogoChip logos={b.logos} /> })),
            ]}
            footerAction={{ label: '＋ ブランド新規登録', onClick: onCreateBrand }}
            onChange={(value) => {
              const selected = brands.find(b => b.id === value);
              setEditForm({
                ...editForm,
                brand_id: value,
                brand_name: selected?.brand_name || editForm.brand_name,
              });
            }}
          />
          {brandBubble}
        </td>
        <th>ブランド（自由入力）</th>
        <td>
          <input className="inline-edit-input" value={editForm.brand_name} onChange={(e) => update('brand_name', e.target.value)} placeholder="ブランド（自由入力）" aria-label="ブランド（自由入力）" />
        </td>
      </tr>
      {brandNote && (
        <tr className="brand-note-row">
          <td colSpan={4}>{brandNote}</td>
        </tr>
      )}
    </>
  );
  return (
    <>
      {brandFirst && brandRow}
      {/* 商品名 */}
      <tr>
        <th>{brandConfig.labels.productName} <span className="required-mark">*</span></th>
        <td colSpan={3}>
          <input className={`inline-edit-input${validationErrors.has('title') ? ' error' : ''}`} value={editForm.title} onChange={(e) => update('title', e.target.value)} placeholder={brandConfig.labels.productName} />
          {validationErrors.has('title') && <span className="inline-edit-error">{brandConfig.labels.productName}は必須です</span>}
        </td>
      </tr>

      {/* 説明 */}
      <tr>
        <th>{brandConfig.labels.productDescription}</th>
        <td colSpan={3}>
          <textarea className="inline-edit-input" rows={6} value={editForm.description} onChange={(e) => update('description', e.target.value)} placeholder={brandConfig.labels.productDescription} />
        </td>
      </tr>

      {/* SKU + JANコード */}
      {brandConfig.features.skuFields && (
        <tr>
          <th>SKU</th>
          <td><input className="inline-edit-input" value={editForm.sku} onChange={(e) => update('sku', e.target.value)} placeholder="SKU" /></td>
          <th>JANコード</th>
          <td>
            <input className="inline-edit-input" value={editForm.barcode_value} onChange={(e) => update('barcode_value', e.target.value)} placeholder="JANコード" />
          </td>
        </tr>
      )}

      {!brandFirst && brandRow}

      {/* カテゴリ + 商品種別 */}
      <tr>
        <th>カテゴリ</th>
        <td><input className="inline-edit-input" value={editForm.category} onChange={(e) => update('category', e.target.value)} placeholder="カテゴリ（カンマ区切り）" /></td>
        <th>{brandConfig.labels.productShort}種別</th>
        <td><input className="inline-edit-input" value={editForm.product_type} onChange={(e) => update('product_type', e.target.value)} placeholder={`${brandConfig.labels.productShort}種別`} /></td>
      </tr>

      {/* 特定カテゴリ（薬機法対応・複数選択可） */}
      <tr>
        <th>特定カテゴリ</th>
        <td colSpan={3}>
          <div className="industry-checkbox-group">
            {INDUSTRY_CATEGORIES.map((key) => (
              <label key={key} className="industry-checkbox-label">
                <input
                  type="checkbox"
                  checked={editForm.industry_categories.includes(key)}
                  onChange={(e) => toggleIndustry(key, e.target.checked)}
                />
                <span>{INDUSTRY_CATEGORY_LABELS[key]}</span>
              </label>
            ))}
          </div>
        </td>
      </tr>

      {/* 景表法のケア (ルールごとのチェックボックス・チェック = 有効) */}
      {/* 未設定キーは「有効」として表示・保存 (該当ルールはデフォルトで自動有効) */}
      {complianceCandidates.length > 0 && (
        <tr>
          <th>景表法のケア</th>
          <td colSpan={3}>
            <div className="industry-checkbox-group">
              {complianceCandidates.map((rule) => {
                const v = editForm.compliance_defaults[rule.code];
                // 未設定 = 有効扱い
                const isEnabled = v !== false;
                return (
                  // 保存ボタンで確定するフォームなのでトグルでなくチェックボックス (業種欄と揃える)
                  <label key={rule.code} className="industry-checkbox-label">
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={(e) => updateComplianceDefault(rule.code, e.target.checked)}
                    />
                    <span>{rule.name}</span>
                  </label>
                );
              })}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
